import React, { useState, useEffect } from 'react';
import fetchClient from 'interfaces/fetchClient';


interface CustomField {
	label: string;
	key: string;
	type: 'text' | 'number' | 'date' | 'boolean' | 'textarea';
	required: boolean;
	defaultValue?: any;
}

interface ProductionConfigResponse {
	_id: string;
	ownerId: string;
	nbSteps: 1 | 2 | 3;
	customStepName?: string;
	customFields: CustomField[];
}

/**
 * Component for administering the production workflow configuration
 * Allows setting the number of steps and custom fields
 */
const ProductionWorkflowConfig: React.FC = () => {
	const [config, setConfig] = useState<ProductionConfigResponse | null>(null);
	const [nbSteps, setNbSteps] = useState<1 | 2 | 3>(1);
	const [customStepName, setCustomStepName] = useState('');
	const [customFields, setCustomFields] = useState<CustomField[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadingConfig, setLoadingConfig] = useState(true);
	const [successMessage, setSuccessMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');

	useEffect(() => {
		fetchConfig();
	}, []);

	const fetchConfig = async () => {
		try {
			const response = await fetchClient.get('/productions/config/workflow', {
				headers: {
					'Authorization': 'Bearer ' + localStorage.getItem('token'),
				},
			});

			const data = response.data;
			setConfig(data);
			setNbSteps(data.nbSteps);
			setCustomStepName(data.customStepName || '');
			setCustomFields(data.customFields || []);
		} catch (error) {
			console.error('Error fetching config:', error);
			setErrorMessage('Impossible de charger la configuration');
		} finally {
			setLoadingConfig(false);
		}
	};

	const addField = () => {
		const newField: CustomField = {
			label: '',
			key: `field_${Date.now()}`,
			type: 'text',
			required: false,
		};
		setCustomFields([...customFields, newField]);
	};

	const updateField = (index: number, updates: Partial<CustomField>) => {
		const updated = [...customFields];
		updated[index] = { ...updated[index], ...updates };
		setCustomFields(updated);
	};

	const removeField = (index: number) => {
		setCustomFields(customFields.filter((_, i) => i !== index));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		setSuccessMessage('');
		setErrorMessage('');

		try {
			const response = await fetchClient.put('/productions/config/workflow', 
				{
					nbSteps,
					customStepName: customStepName || undefined,
					customFields,
				},
				{
					headers: {
						'Authorization': 'Bearer ' + localStorage.getItem('token'),
					},
				}
			);

			const result = response.data;

			setSuccessMessage('Configuration enregistrée avec succès');
			setConfig(result.config);
		} catch (error) {
			console.error('Error saving config:', error);
			setErrorMessage('Erreur de connexion au serveur');
		} finally {
			setLoading(false);
		}
	};

	if (loadingConfig) {
		return (
			<div className="flex items-center justify-center p-8">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
			</div>
		);
	}

	return (
		<div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
			<h2 className="text-2xl font-bold text-gray-800 mb-6">
				Configuration du Workflow de Production
			</h2>

			{successMessage && (
				<div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
					{successMessage}
				</div>
			)}

			{errorMessage && (
				<div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
					{errorMessage}
				</div>
			)}

			<form onSubmit={handleSubmit} className="space-y-8">
				{/* Number of steps */}
				<div className="space-y-2">
					<label className="block text-sm font-medium text-gray-700">
						Nombre d'étapes du workflow
					</label>
					<select
						value={nbSteps}
						onChange={(e) => setNbSteps(parseInt(e.target.value) as 1 | 2 | 3)}
						className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
					>
						<option value={1}>1 étape (Planification → Terminé)</option>
						<option value={2}>2 étapes (Planification → Étape personnalisée → Terminé)</option>
						<option value={3}>3 étapes (Planification → Étape personnalisée → Validation → Terminé)</option>
					</select>
					<p className="text-xs text-gray-500">
						⚠️ La mise à jour du stock se fait uniquement à la dernière étape (Terminé)
					</p>
				</div>

				{/* Custom step name */}
				{nbSteps > 1 && (
					<div className="space-y-2">
						<label className="block text-sm font-medium text-gray-700">
							Nom de l'étape personnalisée
						</label>
						<input
							type="text"
							value={customStepName}
							onChange={(e) => setCustomStepName(e.target.value)}
							placeholder="Ex: Contrôle Qualité, Stérilisation, Validation..."
							className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
						/>
					</div>
				)}

				{/* Custom fields */}
				{nbSteps > 1 && (
					<div className="space-y-4">
						<div className="flex items-center justify-between">
							<label className="block text-sm font-medium text-gray-700">
								Champs personnalisés pour l'étape intermédiaire
							</label>
							<button
								type="button"
								onClick={addField}
								className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
							>
								+ Ajouter un champ
							</button>
						</div>

						{customFields.length === 0 ? (
							<div className="text-center py-6 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
								Aucun champ personnalisé. Cliquez sur "Ajouter un champ" pour en créer.
							</div>
						) : (
							<div className="space-y-4">
								{customFields.map((field, index) => (
									<div
										key={field.key}
										className="p-4 border border-gray-300 rounded-lg space-y-3 bg-gray-50"
									>
										<div className="flex items-start justify-between">
											<h4 className="text-sm font-semibold text-gray-700">
												Champ #{index + 1}
											</h4>
											<button
												type="button"
												onClick={() => removeField(index)}
												className="text-red-600 hover:text-red-800 text-sm"
											>
												Supprimer
											</button>
										</div>

										<div className="grid grid-cols-2 gap-4">
											<div>
												<label className="block text-xs text-gray-600 mb-1">
													Label du champ
												</label>
												<input
													type="text"
													value={field.label}
													onChange={(e) =>
														updateField(index, { label: e.target.value })
													}
													placeholder="Ex: Température, pH, Commentaire..."
													className="w-full px-3 py-2 rounded border border-gray-300 text-sm"
												/>
											</div>

											<div>
												<label className="block text-xs text-gray-600 mb-1">
													Type de champ
												</label>
												<select
													value={field.type}
													onChange={(e) =>
														updateField(index, {
															type: e.target.value as CustomField['type'],
														})
													}
													className="w-full px-3 py-2 rounded border border-gray-300 text-sm"
												>
													<option value="text">Texte</option>
													<option value="number">Nombre</option>
													<option value="date">Date</option>
													<option value="boolean">Oui/Non</option>
													<option value="textarea">Zone de texte</option>
												</select>
											</div>

											<div>
												<label className="block text-xs text-gray-600 mb-1">
													Clé technique (unique)
												</label>
												<input
													type="text"
													value={field.key}
													disabled
													placeholder="Générée automatiquement"
													className="w-full px-3 py-2 rounded border border-gray-300 text-sm bg-gray-100 cursor-not-allowed"
													title={`Clé: ${field.key}`}
												/>
												<p className="text-xs text-gray-500 mt-1">
													Identifiant unique non modifiable
												</p>
											</div>

											<div className="flex items-center space-x-2">
												<input
													type="checkbox"
													checked={field.required}
													onChange={(e) =>
														updateField(index, { required: e.target.checked })
													}
													className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
												/>
												<label className="text-xs text-gray-600">
													Champ obligatoire
												</label>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				)}

				{/* Actions */}
				<div className="flex items-center justify-end space-x-4 pt-4 border-t">
					<button
						type="submit"
						disabled={loading}
						className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
					>
						{loading ? (
							<>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
								<span>Enregistrement...</span>
							</>
						) : (
							<span>Enregistrer la configuration</span>
						)}
					</button>
				</div>
			</form>

			{/* Workflow overview */}
			<div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
				<h3 className="text-sm font-semibold text-blue-900 mb-3">
					📋 Aperçu du workflow actuel :
				</h3>
				<div className="flex items-center justify-center space-x-2 text-sm">
					<span className="px-3 py-1 bg-blue-600 text-white rounded">Planification</span>
					<span className="text-blue-600">→</span>
					{nbSteps >= 2 && (
						<>
							<span className="px-3 py-1 bg-purple-600 text-white rounded">
								{customStepName || 'Étape personnalisée'}
							</span>
							<span className="text-blue-600">→</span>
						</>
					)}
					{nbSteps === 3 && (
						<>
							<span className="px-3 py-1 bg-orange-600 text-white rounded">
								Validation
							</span>
							<span className="text-blue-600">→</span>
						</>
					)}
					<span className="px-3 py-1 bg-green-600 text-white rounded">
						Terminé (+ Mise à jour stock)
					</span>
				</div>
			</div>
		</div>
	);
};

export default ProductionWorkflowConfig;
